import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import { type SeatBearingAccount, noteNoticeFor } from './linkedin-seat.js';

// ---------------------------------------------------------------------------
// Telling someone what will happen to the connection note they are typing
//
// From 4 to 15 August 2026 a live campaign sent zero invitations for eleven
// days. Every one carried a personal note, every one was refused, and the only
// signal anywhere was a provider error naming the ACCOUNT. Nothing on any
// screen said that a note is a scarce monthly allowance on a free seat. The
// person writing the note had no way to know it was why nobody was reached.
//
// SHOWN ONLY WHEN IT APPLIES
//
// A connect step, with a note actually written in it, and at least one sending
// account that is not a paid seat. All three, or nothing renders — a notice
// that appears when it does not apply is a notice people learn to dismiss.
//
// IT SAYS WHY, NOT JUST WHAT
//
// Three different reasons, not one general rule: notes switched off entirely,
// the free seat's allowance already spent this month, or a free seat that still
// has one but will run out. Only the middle one describes something the reader
// can see today, and it is the live state as of 17 August.
//
// NO NUMBER APPEARS HERE
//
// LinkedIn publishes five a month for a free seat; our own measurement says at
// least two. They do not agree, we have never measured the boundary, and a
// figure on screen would read as a promise the product cannot keep.
// ---------------------------------------------------------------------------

export interface ConnectionNoteNoticeProps {
  /**
   * Whether a connect step with a note in it is actually on screen. The host
   * owns this: only it knows the step. False renders nothing at all.
   */
  hasNoteOnConnectStep: boolean;
  /**
   * The LinkedIn accounts THIS campaign sends from — the wizard draft's
   * selection, or the saved campaign's. Required, because the lookup below
   * returns every account the workspace has and the sentence is about one
   * campaign. Empty renders nothing, which is the honest answer while the
   * Sales Rep step is still unanswered.
   */
  sendingAccountIds: readonly string[];
}

interface AccountsResponse {
  ok?: boolean;
  accounts?: SeatBearingAccount[];
  /** Absent on an older server; see the undefined handling below. */
  invitationsCarryANote?: boolean;
  /** The scheduler's own idea of "this month", in UTC. */
  rationMonth?: string;
}

/** The month the note allowance is measured in, when the next reset lands. */
function nextMonthLabel(rationMonth: string): string {
  const [year, month] = rationMonth.split('-').map(Number);
  if (!year || !month) return 'the start of next month';
  const next = month === 12 ? { y: year + 1, m: 1 } : { y: year, m: month + 1 };
  const names = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  return `1 ${names[next.m - 1]}`;
}

export const ConnectionNoteNotice = stateful((props: ConnectionNoteNoticeProps) => {
  const [accounts, setAccounts] = useState<SeatBearingAccount[]>([]);
  const [notesEnabled, setNotesEnabled] = useState<boolean | undefined>(undefined);
  const [thisMonth, setThisMonth] = useState<string | undefined>(undefined);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    void (async () => {
      const data: AccountsResponse = await fetch('/api/accounts/linkedin')
        .then(r => r.json())
        .catch(() => ({ ok: false }));
      if (!data.ok) {
        // A lookup that failed is not evidence that everything is fine, but it
        // is also not evidence of anything else. Say nothing rather than invent
        // a state — a notice that appears when the server is merely unreachable
        // teaches people to dismiss it.
        setLoaded(true);
        return;
      }
      setAccounts(data.accounts ?? []);
      setNotesEnabled(data.invitationsCarryANote);
      setThisMonth(data.rationMonth);
      setLoaded(true);
    })();
  }, []);

  // `undefined` means the field was absent — an older server, or a failed
  // lookup. Treated as "not observed" and shown as nothing at all, for the same
  // reason an unread seat is never rendered as "free": a guess here is a
  // sentence a person will act on.
  if (!loaded || notesEnabled === undefined || thisMonth === undefined) return nothing;

  const notice = noteNoticeFor(accounts, {
    notesEnabled,
    hasNoteOnConnectStep: props.hasNoteOnConnectStep,
    thisMonth,
    sendingAccountIds: props.sendingAccountIds,
  });
  if (!notice.show) return nothing;

  const names = notice.accountNames.join(', ');

  const body =
    notice.kind === 'switched_off'
      ? html`
        <span>
          <strong>The personal note on this step will not be sent.</strong>
          Notes on invitations are switched off for every account right now. The invitation still goes out, and the
          message steps that follow are unaffected.
        </span>
      `
      : notice.kind === 'spent'
        ? html`
          <span>
            <strong>The personal note on this step will not be sent.</strong>
            ${names} ${notice.accountNames.length === 1 ? 'is a free LinkedIn account' : 'are free LinkedIn accounts'},
            and LinkedIn allows a free account only a small number of note-carrying invitations per month — already
            used up this month. The invitation still goes out, without the note, so the lead is still reached. Notes
            become available again on ${nextMonthLabel(thisMonth)}.
          </span>
        `
        : html`
          <span>
            <strong>These LinkedIn accounts are not Premium:</strong> ${names}. LinkedIn allows a free account only a
            small number of note-carrying invitations per month. Once that is used up, invitations from it still go
            out — without the note — so the lead is still reached.
          </span>
        `;

  // `alert-info`, not `alert-warning`: nothing here is a mistake the person
  // made and nothing needs correcting by them. It is how LinkedIn works.
  return html`
    <div class="alert alert-info items-start text-sm">
      <span class="icon-[material-symbols--info-outline] mt-0.5 shrink-0 text-lg"></span>
      ${body}
    </div>
  `;
});
